/**
 * authLandingGuard — Q5 (decision 2026-07-20, option i): a magic link must NOT silently replace an
 * existing sign-in.
 *
 * The problem it solves: GoTrue's `detectSessionInUrl` exchanges the auth params on the landing URL
 * for a session *as the client is constructed* — before any app code runs. So when an admin clicked
 * an item-share invite they had generated, their own session was already gone by the time
 * `WrongAccountPrompt` appeared; dismissing it (or just navigating away) left them signed in as the
 * invitee. The prompt was advisory about something that had already happened.
 *
 * The fix: run BEFORE `createClient`. If the URL carries auth params *and* a different,
 * non-anonymous session is already persisted, strip those params off the URL (so detectSessionInUrl
 * finds nothing and the existing session survives) and stash them. The app then shows the prompt
 * FIRST, and only on an explicit "sign out & continue" do we sign out and apply the stashed tokens.
 *
 * Everything here is pure except `installAuthLandingGuard`, which does the stashing/stripping — so
 * the detection rules are unit-testable without a browser or a Supabase client.
 */

import { isHalfConfirmedEmailChange, recordLandingNotice, EMAIL_CHANGE_HALF, EMAIL_CHANGED } from './authLandingNotice.js';

export const DEFERRED_KEY = 'arcos.deferredAuthLanding';
export const LANDING_ERROR_KEY = 'arcos.authLandingError';

// ── Merging the anonymous account when a sign-in LINK lands ─────────────────────────────────────
//
// Signing in by typing the emailed code merges the browser's anonymous account into the real one
// (verifyEmailSignIn → merge_account), so hearts, events added and — since 2026-10-08 — Ask chats
// made while signed out follow the person. Clicking the link in the same email did not: the page
// that lands never learned which anonymous account it had been. These keys carry that across.
//
// The merge is BOUND TO A SIGN-IN THIS BROWSER ASKED FOR, and that binding is the point, not a
// detail. Without it, a magic link to someone else's account — sent to you, clicked by you — would
// fold your anonymous chats and saves into THEIR account. So startEmailSignIn records which
// anonymous account asked, and for which address (SIGN_IN_REQUEST_KEY, localStorage, so the link
// may open in another tab); the guard turns that into a pending merge only when the anonymous
// session that lands is the one that asked (PENDING_MERGE_KEY, sessionStorage); and the merge runs
// only once the session is the same address, signed in. A link this browser never requested signs
// in exactly as before, with nothing merged.
export const SIGN_IN_REQUEST_KEY = 'arcos.signInRequest';
export const PENDING_MERGE_KEY = 'arcos.pendingAnonMerge';
/** A sign-in request older than this no longer binds a landing (GoTrue's own link lives an hour). */
export const SIGN_IN_REQUEST_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const sameEmail = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

/** The sign-in this browser asked for — `{anon, email, at}` — or null. Tolerant of junk. */
export function readSignInRequest(storage) {
  let raw;
  try { raw = storage?.getItem?.(SIGN_IN_REQUEST_KEY); } catch { return null; }
  if (!raw) return null;
  try {
    const r = JSON.parse(raw);
    return r && typeof r.anon === 'string' && Number.isFinite(r.at) ? r : null;
  } catch { return null; }
}

/**
 * Which anonymous account a landing should merge once signed in, or null. All four must hold: the
 * URL carries a credential; the session it lands over is anonymous; that anonymous account is the
 * one that asked for a sign-in email from this browser; and the request is recent.
 * @returns {{from: string, email: string|null}|null}
 */
export function pendingMergeFrom(persisted, authParams, request, now = Date.now()) {
  if (!isCredential(authParams)) return null;
  if (!persisted?.userId || !persisted.isAnonymous) return null;
  if (!request || request.anon !== persisted.userId) return null;
  if (!(now - request.at >= 0 && now - request.at <= SIGN_IN_REQUEST_MAX_AGE_MS)) return null;
  return { from: persisted.userId, email: request.email || null };
}

/**
 * What to do with a pending merge, given the session now in hand:
 *   'none'  — nothing pending
 *   'wait'  — not signed in yet (still anonymous, or no session); keep it
 *   'drop'  — signed in, but not as the address that asked, or as the SAME account (a claim
 *             re-binds the anonymous id rather than replacing it); forget it
 *   'merge' — signed in as the address that asked, under a different account; merge
 */
export function anonMergeDecision(pending, user) {
  if (!pending?.from) return 'none';
  if (!user?.id || user.is_anonymous) return 'wait';
  if (user.id === pending.from) return 'drop';
  if (pending.email && !sameEmail(pending.email, user.email)) return 'drop';
  return 'merge';
}

export function getPendingAnonMerge(win = globalThis) {
  try {
    const raw = win.sessionStorage?.getItem(PENDING_MERGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearPendingAnonMerge(win = globalThis) {
  try { win.sessionStorage?.removeItem(PENDING_MERGE_KEY); } catch { /* non-fatal */ }
}

export function clearSignInRequest(win = globalThis) {
  try { win.localStorage?.removeItem(SIGN_IN_REQUEST_KEY); } catch { /* non-fatal */ }
}

/**
 * Pull Supabase auth params off a landing URL. Three shapes, in precedence order:
 *
 *   implicit — magic-link tokens in the HASH (`#access_token=…&refresh_token=…`)
 *   pkce     — a `code` in the QUERY
 *   error    — GoTrue REJECTED the credential and redirected here to say so
 *
 * The error shape is the one that was missing, and its absence is why an expired invite used to
 * degrade into a not-found page: GoTrue's rejection redirect looks like an ordinary navigation to
 * everything downstream, so nothing knew a sign-in had even been attempted, let alone failed. The
 * app then minted an anonymous session and let the surface rule decide, five seconds later, on the
 * basis of "not a member". Reading the failure here means we know at t=0 and can say WHY.
 *
 * GoTrue puts the reason in the hash for the implicit flow and the query for PKCE, so check both —
 * mirroring where each success shape arrives. Success is checked FIRST so a URL that somehow
 * carries both never loses a usable session to an error branch.
 *
 * @param {{hash?: string, search?: string}} loc a Location-like object
 * @returns {{kind:'implicit'|'pkce'|'error', …}|null}
 */
export function extractAuthParams(loc) {
  const hash = new URLSearchParams((loc?.hash || '').replace(/^#/, ''));
  const access_token = hash.get('access_token');
  const refresh_token = hash.get('refresh_token');
  if (access_token && refresh_token) {
    return { kind: 'implicit', access_token, refresh_token, type: hash.get('type') || null };
  }
  const query = new URLSearchParams(loc?.search || '');
  const code = query.get('code');
  if (code) return { kind: 'pkce', code };

  // GoTrue's failure redirect — matched NARROWLY, because `?error=` is not ours alone.
  //
  // The collision that forced this: api/stripe-connect/callback.js sends its own failures to
  // `?tab=payments&error=<message>`, which PaymentsTab surfaces and then strips. A loose "any
  // `error` param is an auth rejection" test would have swallowed that param here — before
  // PaymentsTab ever ran — and silently killed every Stripe Connect error message.
  //
  // So: in the QUERY (PKCE flow) require `error_code`, a name GoTrue owns and nobody else here
  // uses. In the HASH (implicit flow) accept `error_code`, or a bare `error` whose value is one of
  // OAuth 2's own codes — the fragment is never used for app-level messaging, and the allowlist
  // keeps even that from over-matching.
  const errorCode = hash.get('error_code') || query.get('error_code');
  const hashError = hash.get('error');
  const isOauthCode = OAUTH_ERROR_CODES.has(hashError || '');
  if (errorCode || isOauthCode) {
    return {
      kind: 'error',
      error: hashError || query.get('error') || null,
      errorCode: errorCode || null,
      description: hash.get('error_description') || query.get('error_description') || null,
    };
  }
  return null;
}

/** The `error` values GoTrue actually emits (OAuth 2 §4.1.2.1 plus Supabase's own). */
const OAUTH_ERROR_CODES = new Set([
  'access_denied', 'server_error', 'temporarily_unavailable',
  'unauthorized_client', 'invalid_request', 'invalid_scope', 'unsupported_response_type',
]);

/** Does this landing carry a usable credential (as opposed to a rejection)? */
function isCredential(authParams) {
  return !!authParams && authParams.kind !== 'error';
}

/**
 * Read the persisted Supabase session without instantiating a client (we must decide before the
 * client exists). Tolerant of every shape of junk: a missing key, unparseable JSON, or a session
 * without a user all read as "nobody is signed in".
 * @param {Storage} storage localStorage-like
 * @param {string} key the `sb-<ref>-auth-token` key
 * @returns {{userId: string|null, isAnonymous: boolean}|null}
 */
export function readPersistedSession(storage, key) {
  let raw;
  try { raw = storage?.getItem?.(key); } catch { return null; }   // storage can throw (Safari private)
  if (!raw) return null;
  let parsed;
  try { parsed = JSON.parse(raw); } catch { return null; }
  // supabase-js has stored this as both the session object and {currentSession: …} across versions.
  const session = parsed?.currentSession || parsed;
  const user = session?.user;
  if (!user?.id) return null;
  return { userId: user.id, isAnonymous: !!user.is_anonymous };
}

/**
 * The rule, now with a REASON. Two deferrals share the same hold-back machinery:
 *
 * 'wrong-account' (Q5, unchanged): the URL is trying to sign somebody in AND a real
 * (non-anonymous) session is already persisted — protect it, prompt first.
 *
 * 'consent' (R4-1): an ITEM-SHARE claim landing (`/item/…?ct=…`) with NO protected session.
 * Before R4-1 the magic link signed the recipient in — and thereby converted their placeholder
 * to a real Arcos account — the instant the page loaded, with no consent moment anywhere.
 * Now the tokens are held and the item page renders a consent gate ("viewing this verifies
 * your email and creates your free Arcos account"); only the explicit click applies them.
 * This deliberately fires over an ANONYMOUS session too: the anon→signed-in upgrade path is
 * fine for people who ASKED to sign in, but an emailed share link is not that.
 *
 * Deliberately NOT deferred:
 *  • no auth params — an ordinary navigation;
 *  • a sign-in landing anywhere else with no session — the user initiated that sign-in.
 *  • an ERROR landing (kind:'error') — there is no credential to hold back. Deferring one would be
 *    actively wrong: the whole mechanism exists to stop a magic link REPLACING a session, and a
 *    rejected link replaces nothing. Without this guard, an admin who clicks a stale invite while
 *    signed in would be told they were about to be switched to another account — a prompt about
 *    something that cannot happen. The error is stashed separately (LANDING_ERROR_KEY) instead.
 * A link for the SAME account is 'same-account', not a switch: nothing would be replaced, so the
 * installer drops the link and keeps the session it has. This used to defer as 'wrong-account' on
 * the belief that identities cannot be compared before the exchange — but an implicit-flow link
 * carries its account in the access token's `sub`. Reading it unverified is safe for this one
 * decision, because the only thing a matching `sub` does is DISCARD the link: no token from the URL
 * is ever applied on its say-so. A PKCE `code` carries no identity, so it still defers.
 * (2026-10-08: members opening their own sign-in link, in the browser they were already signed
 * into, were told it "was sent to a different email".)
 *
 * @returns {'same-account'|'wrong-account'|'consent'|null}
 */
export function deferReason(persisted, authParams, loc) {
  if (!isCredential(authParams)) return null;
  if (persisted && !persisted.isAnonymous) {
    if (authParams.kind === 'implicit' && persisted.userId && tokenSubject(authParams.access_token) === persisted.userId) {
      return 'same-account';
    }
    return 'wrong-account';
  }
  const onItemPage = /^\/item\//.test(loc?.pathname || '');
  const hasCt = new URLSearchParams(loc?.search || '').has('ct');
  if (onItemPage && hasCt) return 'consent';
  return null;
}

/** The `sub` claim of a JWT, read WITHOUT verifying it — see deferReason for why that is enough. */
export function tokenSubject(jwt) {
  try {
    const part = String(jwt || '').split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    const sub = JSON.parse(atob(b64))?.sub;
    return typeof sub === 'string' && sub ? sub : null;
  } catch { return null; }
}

/** Back-compat shim for the original Q5 rule (kept for its tests + any external callers). */
export function shouldDeferAuthLanding(persisted, authParams) {
  return deferReason(persisted, authParams, { pathname: '/', search: '' }) === 'wrong-account';
}

/**
 * Derive the localStorage key supabase-js uses: `sb-<ref>-auth-token`, where <ref> is the first
 * hostname label — mirroring the library's own default so we read the same slot it writes
 * (`abcdefgh.supabase.co` → `sb-abcdefgh-…`; the local stack's `127.0.0.1` → `sb-127-…`).
 * Returns null on an unparseable URL, which makes the guard no-op — failing safe (stock behavior)
 * rather than reading the wrong slot and wrongly deciding nobody is signed in.
 */
export function authStorageKey(supabaseUrl) {
  try {
    const ref = new URL(String(supabaseUrl)).hostname.split('.')[0];
    return ref ? `sb-${ref}-auth-token` : null;
  } catch { return null; }
}

/**
 * Strip the auth params from the visible URL so detectSessionInUrl can't consume them.
 *
 * Note what is NOT stripped: `ct`. The claim token is OUR credential and outlives GoTrue's by up to
 * fourteen days — it is precisely what the fallback redeems once the person signs in again, so it
 * has to survive both this cleanup and the reload that follows a fresh sign-in.
 */
function stripAuthParamsFromUrl(win) {
  const url = new URL(win.location.href);
  url.hash = '';
  url.searchParams.delete('code');
  win.history.replaceState({}, '', url.toString());
}

/**
 * Strip GoTrue's rejection params. Same idea as above, different reason: nothing downstream can
 * consume them, but leaving `#error=access_denied&error_code=otp_expired&…` in the address bar
 * means a reload replays a failure we have already handled, and it puts raw auth vocabulary in
 * front of someone who just wanted to accept an invitation.
 */
function stripAuthErrorFromUrl(win) {
  const url = new URL(win.location.href);
  url.hash = '';
  for (const k of ['error', 'error_code', 'error_description']) url.searchParams.delete(k);
  win.history.replaceState({}, '', url.toString());
}

/**
 * Install the guard. Call ONCE, before createClient.
 * @returns {boolean} true when a landing was deferred (the app should prompt before proceeding)
 */
export function installAuthLandingGuard(win, supabaseUrl) {
  try {
    const key = authStorageKey(supabaseUrl);
    if (!key || !win?.location) return false;
    const authParams = extractAuthParams(win.location);

    // The first of the two email-change links (secure email change): GoTrue sends the person back
    // with only a message in the hash. Note it so the app can say the other inbox is still to go,
    // and clean the URL. See authLandingNotice.js.
    if (!authParams && isHalfConfirmedEmailChange(win.location)) {
      recordLandingNotice(win, EMAIL_CHANGE_HALF);
      stripAuthParamsFromUrl(win);
      return false;
    }

    // GoTrue rejected the credential (expired OTP, already-consumed link, denied access). Record
    // WHY and clean the URL. Handled before the defer rules because there is nothing to defer —
    // and recorded rather than acted on here, since this runs before the Supabase client exists
    // and the response depends on a claim-token probe the client has to make.
    if (authParams?.kind === 'error') {
      win.sessionStorage.setItem(LANDING_ERROR_KEY, JSON.stringify({
        error: authParams.error,
        errorCode: authParams.errorCode,
        description: authParams.description,
      }));
      stripAuthErrorFromUrl(win);
      return false;
    }

    const persisted = readPersistedSession(win.localStorage, key);
    const reason = deferReason(persisted, authParams, win.location);
    // Already signed in as the account the link is for: keep that session, drop the link — except
    // when the link completes an email change. Its session is the same account carrying its NEW
    // address, and the one in storage still has the old; keeping it left the old email on screen
    // for up to an hour. Leave the link for supabase-js to take, and have the app say it worked.
    if (reason === 'same-account') {
      if (authParams.type === 'email_change') { recordLandingNotice(win, EMAIL_CHANGED); return false; }
      stripAuthParamsFromUrl(win);
      return false;
    }
    // Opened on a device with no session (or an anonymous one): it signs in as the changed account.
    if (!reason && authParams?.kind === 'implicit' && authParams.type === 'email_change') {
      recordLandingNotice(win, EMAIL_CHANGED);
    }

    // A sign-in link this browser asked for, landing over the anonymous session that asked: note it,
    // so the anonymous account is merged once the session switches (supabase.js). Never for a
    // 'consent' landing — that is someone else's invitation, not a sign-in this browser requested.
    if (reason !== 'consent') {
      const merge = pendingMergeFrom(persisted, authParams, readSignInRequest(win.localStorage));
      if (merge) win.sessionStorage.setItem(PENDING_MERGE_KEY, JSON.stringify({ ...merge, at: Date.now() }));
    }

    if (!reason) return false;

    // sessionStorage: tab-scoped and cleared when the tab closes — a stashed credential must not
    // outlive the visit that produced it. `reason` tells consumers which surface owns the prompt:
    // 'wrong-account' → WrongAccountPrompt (WorkspaceRouter) · 'consent' → the item-page gate.
    win.sessionStorage.setItem(DEFERRED_KEY, JSON.stringify({ ...authParams, reason }));
    stripAuthParamsFromUrl(win);
    return true;
  } catch (err) {
    // Never let the guard break the app: on any failure fall through to stock behavior.
    console.warn('[auth] landing guard skipped:', err?.message || err);
    return false;
  }
}

/** The stashed landing, if one was deferred this visit. */
export function getDeferredAuthLanding(win = globalThis) {
  try {
    const raw = win.sessionStorage?.getItem(DEFERRED_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearDeferredAuthLanding(win = globalThis) {
  try { win.sessionStorage?.removeItem(DEFERRED_KEY); } catch { /* non-fatal */ }
}

/**
 * The auth rejection this visit landed with, if any — `{error, errorCode, description}`.
 * Set by the guard before the Supabase client exists; read by the claim landing, which pairs it
 * with a `?ct=` probe to decide between "your invitation is still good", "it expired", and
 * "you already accepted".
 */
export function getAuthLandingError(win = globalThis) {
  try {
    const raw = win.sessionStorage?.getItem(LANDING_ERROR_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearAuthLandingError(win = globalThis) {
  try { win.sessionStorage?.removeItem(LANDING_ERROR_KEY); } catch { /* non-fatal */ }
}
